import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Observable, BehaviorSubject, of, Subject } from 'rxjs';
import { map, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';

import { loadSuggestedProfiles } from 'src/app/core/services/suggestions/suggestions.actions';
import {
  State as SuggestionsState,
  selectSuggestedProfiles
} from 'src/app/core/services/suggestions/suggestions.reducer';
import { Profile } from 'src/app/core/services/suggestions/suggestions.model';
import {
  loadSummerProgram,
  loadAfterSchool,
  loadJobs
} from 'src/app/core/services/eduniverse/eduniverse.actions';
import {
  State as EduniverseState,
  selectSummerPrograms,
  selectAfterSchoolPrograms,
  selectJobs
} from 'src/app/core/services/eduniverse/eduniverse.reducer';
import { SummerProgram } from 'src/app/core/services/eduniverse/eduniverse.model';
import { State as ContactsState } from 'src/app/core/services/suggestions/suggestions.reducer';
import { Group } from 'src/app/core/services/groups/group.model';
import {
  State as GroupsState,
  selectGroups
} from 'src/app/core/services/groups/groups.reducer';
import {
  searchGroups,
  subscribeTo,
  unsubscribeFrom,
  unrequestGroup
} from 'src/app/core/services/groups/groups.actions';
import {
  State as EventsState,
  selectEvents
} from 'src/app/core/services/events/events.reducer';
import { searchEvents } from 'src/app/core/services/events/events.actions';
import { Event } from 'src/app/core/services/events/event.model';
import {
  State as DiscussionsState,
  selectDiscussions
} from 'src/app/core/services/discussions/discussions.reducer';
import { searchDiscussions } from 'src/app/core/services/discussions/discussions.actions';
import {
  State as OrganizationsState,
  selectOrganizations
} from 'src/app/core/services/organizations/organizations.reducer';
import {
  searchOrganizations,
  orgSubscribeTo,
  orgUnSubscribeFrom
} from 'src/app/core/services/organizations/organizations.actions';

import { getMembers } from 'src/app/core/services/organization/organization.actions';
import {
  State as OrganizationState,
  selectMembers
} from 'src/app/core/services/organization/organization.reducer';

import { Organization } from 'src/app/core/services/organizations/organization.model';
import {
  State as PeopleState,
  selectPeople
} from 'src/app/core/services/people/people.reducer';
import { searchPeople } from 'src/app/core/services/people/people.actions';
import { People } from 'src/app/core/services/people/people.model';
import {
  addContact,
  removeContact
} from 'src/app/core/services/contacts/contacts.actions';
import { IDiscussion } from '../../shared/models/discussion';
import { Member } from 'src/app/core/services/organization/organization.model';

import { searchTop } from 'src/app/core/services/top/top.actions';
import {
  State as TopState,
  selectTopPeople,
  selectTopDiscussions,
  selectTopEvents,
  selectTopGroups,
  selectTopOrganizations,
  selectTop
} from 'src/app/core/services/top/top.reducer';
import { Club } from '../../core/services/clubs/club.model';
import {
  State as ClubsState,
  selectClubs
} from '../../core/services/clubs/clubs.reducer';
import { searchClubs } from '../../core/services/clubs/clubs.actions';
import { AuthService } from 'src/app/core/services/auth.service';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit, OnDestroy {
  static AMOUNT_OF_USERS_ON_ONE_PAGE = 20;

  kinds = SearchComponent.Kind;
  profileTypes = SearchComponent.ProfileType;
  educatorProfileSubTypes: Array<SearchComponent.ProfileSubType> = [
    'teacher',
    'faculty',
    'tutor',
    'coach',
    'mentor',
    'other educator'
  ];

  organizations: Array<SearchComponent.Organization> = [
    'school',
    'pre-school/kindergarten',
    'university',
    'after-school',
    'summer camp/program',
    'tutoring agency',
    'sport academy/clud',
    'education consultancy',
    'other'
  ];

  ageGroups: Array<SearchComponent.AgeGroup> = [
    { id: '1', title: '3-6 years' },
    { id: '2', title: '7-9 years' },
    { id: '3', title: '10-13 years' },
    { id: '4', title: '14-17 years' },
    { id: '5', title: 'any' }
  ];

  searchTerm = '';
  page = 1;
  selectedProfileType$ = new BehaviorSubject<SearchComponent.ProfileType>(null);
  selectedProfileSubType$ = new BehaviorSubject<string>(null);
  organizationMember$ = new BehaviorSubject<SearchComponent.Organization>(null);
  selectedAgeGroup$ = new BehaviorSubject<string>(null);
  queryParams$ = new BehaviorSubject({});

  kind$: BehaviorSubject<SearchComponent.Kind> = new BehaviorSubject(
    this.activatedRout.snapshot.queryParams.kind || this.kinds.Top
  );

  groups$: Observable<{
    totalCount: number;
    items: Group[];
    isLoading: boolean;
  }>;
  people$: Observable<{ count: number; items: People[] }>;
  discussions$: Observable<{ count: number; items: IDiscussion[] }>;
  organizations$: Observable<{ count: number; items: Organization[] }>;
  clubs$: Observable<{ count: number; items: Club[] }>;
  events$: Observable<{ count: number; items: Event[] }>;
  members$: Observable<{ count: number; items: Member[] }>;
  totalAmount$: Observable<number>;
  amountOfPages$: Observable<number>;
  suggestedProfiles$: Observable<Profile[]>;
  summerPrograms$: Observable<{ totalCount: number; items: SummerProgram[] }>;
  afterSchoolPrograms$: Observable<{
    totalCount: number;
    items: SummerProgram[];
  }>;
  jobs$: Observable<{ totalCount: number; items: SummerProgram[] }>;
  top$: Observable<{
    people: any;
    discussions: any;
    events: any;
    groups: any;
    organizations: any;
    clubs: any;
  }>;

  private componentDestroy$ = new Subject();

  constructor(
    private activatedRout: ActivatedRoute,
    private router: Router,
    private readonly auth: AuthService,
    private suggestionsStore: Store<SuggestionsState>,
    private contactsStore: Store<ContactsState>,
    private eduniverseStore: Store<EduniverseState>,
    private groupsStore: Store<GroupsState>,
    private clubsStore: Store<ClubsState>,
    private eventsStore: Store<EventsState>,
    private discussionsStore: Store<DiscussionsState>,
    private organizationsStore: Store<OrganizationsState>,
    private organizationStore: Store<OrganizationState>,
    private peopleStore: Store<PeopleState>,
    private topStore: Store<TopState>
  ) {
    this.activatedRout.queryParams.subscribe(this.onSearchParamsChange);

    this.people$ = this.peopleStore.pipe(select(selectPeople()));
    this.organizations$ = this.organizationsStore.pipe(
      select(selectOrganizations())
    );
    this.discussions$ = this.discussionsStore.pipe(select(selectDiscussions()));
    this.clubs$ = this.discussionsStore.pipe(select(selectClubs()));
    this.groups$ = this.groupsStore.pipe(select(selectGroups()));
    this.events$ = this.eventsStore.pipe(select(selectEvents()));
    this.members$ = this.organizationStore.pipe(select(selectMembers()));

    this.totalAmount$ = this.kind$.pipe(switchMap(this.totalAmountOfResults));
    this.amountOfPages$ = this.kind$.pipe(switchMap(this.updateAmountOfPages));

    this.suggestedProfiles$ = this.suggestionsStore.pipe(
      select(selectSuggestedProfiles())
    );
    this.summerPrograms$ = this.eduniverseStore.pipe(
      select(selectSummerPrograms())
    );
    this.afterSchoolPrograms$ = this.eduniverseStore.pipe(
      select(selectAfterSchoolPrograms())
    );
    this.jobs$ = this.eduniverseStore.pipe(select(selectJobs()));
    this.top$ = this.topStore.pipe(select(selectTop()));
  }

  ngOnInit() {
    this.suggestionsStore.dispatch(loadSuggestedProfiles({ N: 3 }));

    this.eduniverseStore.dispatch(loadSummerProgram({ page: 1, term: '' }));
    this.eduniverseStore.dispatch(loadAfterSchool({ page: 1, term: '' }));
    this.eduniverseStore.dispatch(loadJobs({ page: 1, term: '' }));

    this.activatedRout.queryParams
      .pipe(takeUntil(this.componentDestroy$), tap(this.setQueryParams))
      .subscribe((_) => _);
  }

  ngOnDestroy() {
    this.componentDestroy$.next();
    this.componentDestroy$.complete();
  }

  onPrevPage() {
    this.amountOfPages$.pipe(
      filter((_amountOfPages) => this.page >= 1),
      map((_amountOfPages) =>
        this.router.navigate(['/search'], {
          queryParams: this.updatedQueryParams({ page: this.page + 1 })
        })
      )
    );
  }

  onNextPage() {
    this.amountOfPages$.pipe(
      filter((amountOfPages) => this.page < amountOfPages),
      map((_amountOfPages) =>
        this.router.navigate(['/search'], {
          queryParams: this.updatedQueryParams({ page: this.page + 1 })
        })
      )
    );
  }

  async onSearch() {
    await this.router.navigate(['/search'], {
      queryParams: this.updatedQueryParams({
        q: this.searchTerm,
        kind: this.kind$.value
      })
    });
  }

  async onSetPage(page) {
    const p = Number(page);

    if (!Number.isSafeInteger(p)) {
      return;
    }

    await this.router.navigate(['/search'], {
      queryParams: this.updatedQueryParams({
        ...this.queryParams$.value,
        page: p
      })
    });
  }

  async onSelectKind(kind: SearchComponent.Kind) {
    this.kind$.next(kind);

    this.selectedProfileType$.next(null);
    this.selectedProfileSubType$.next(null);
    this.organizationMember$.next(null);
    this.selectedAgeGroup$.next(null);

    await this.router.navigate(['/search'], {
      queryParams: { kind, q: this.searchTerm }
    });
  }

  async onProfileTypeSelect(
    event: MatCheckboxChange,
    profileType: SearchComponent.ProfileType
  ) {
    this.selectedProfileType$.next(event.checked ? profileType : null);
    this.selectedProfileSubType$.next('');

    await this.router.navigate(['/search'], {
      queryParams: this.updatedQueryParams({ page: 1 })
    });
  }

  async onProfileSubTypeSelect(
    event: MatCheckboxChange,
    profileSubType: SearchComponent.ProfileSubType
  ) {
    this.selectedProfileSubType$.next(event.checked ? profileSubType : '');

    await this.router.navigate(['/search'], {
      queryParams: this.updatedQueryParams({ page: 1 })
    });
  }

  async onOrganizationSelect(
    event: MatCheckboxChange,
    organization: SearchComponent.Organization
  ) {
    this.organizationMember$.next(event.checked ? organization : null);

    await this.router.navigate(['/search'], {
      queryParams: this.updatedQueryParams({
        member: this.organizationMember$.value,
        page: 1
      })
    });
  }

  async onAgeGroupSelect(event: MatCheckboxChange, ageGroup: string) {
    this.selectedAgeGroup$.next(event.checked ? ageGroup : null);

    await this.router.navigate(['/search'], {
      queryParams: this.updatedQueryParams({
        age_group: this.selectedAgeGroup$.value,
        page: 1
      })
    });
  }

  onProfileConnect(id: number) {
    this.contactsStore.dispatch(addContact({ id }));
  }

  onProfileDisconnect(id: number) {
    this.contactsStore.dispatch(removeContact({ id }));
  }

  onOrganizationSubscribe(id: number) {
    this.organizationsStore.dispatch(orgSubscribeTo({ id }));
  }

  onOrganizationUnSubscribe(id: number) {
    this.organizationsStore.dispatch(orgUnSubscribeFrom({ id }));
  }

  onGroupJoin(group: Group) {
    const { id, is_subscriber, is_requested } = group;

    if (!is_subscriber && !is_requested) {
      this.groupsStore.dispatch(subscribeTo({ id }));
    } else if (is_subscriber) {
      this.groupsStore.dispatch(unsubscribeFrom({ id }));
    } else if (is_requested) {
      this.groupsStore.dispatch(
        unrequestGroup({ group_id: id, user_id: this.auth.userID() })
      );
    }
  }

  private setQueryParams = (params) => {
    this.queryParams$.next(params);
  };

  private profileTypeToQueryParam(value: SearchComponent.ProfileType) {
    switch (value) {
      case SearchComponent.ProfileType.Student:
        return 'student[member]';
      case SearchComponent.ProfileType.Educator:
        return 'teacher[member]';
      default:
        return null;
    }
  }

  private profileQueryParamToType(param: string) {
    switch (param) {
      case 'student[member]':
        return SearchComponent.ProfileType.Student;
      case 'teacher[member]':
        return SearchComponent.ProfileType.Educator;
      default:
        return null;
    }
  }

  onSeeMore(type: string) {
    const typeToKind = {
      people: SearchComponent.Kind.People,
      organizations: SearchComponent.Kind.Organizations,
      groups: SearchComponent.Kind.Groups,
      events: SearchComponent.Kind.Events,
      discussions: SearchComponent.Kind.Discussions,
      clubs: SearchComponent.Kind.Clubs
    };

    this.kind$.next(typeToKind[type]);
    this.search({});
  }

  private onSearchParamsChange = (params) => {
    this.kind$.next(this.kindConverter(params.kind));

    if ('student[member]' in params) {
      this.selectedProfileType$.next(
        this.profileQueryParamToType('student[member]')
      );
    } else if ('teacher[member]' in params) {
      this.selectedProfileType$.next(
        this.profileQueryParamToType('teacher[member]')
      );
    }

    if (params['student[member]']) {
      this.selectedProfileSubType$.next(params['student[member]']);
    } else if (params['teacher[member]']) {
      this.selectedProfileSubType$.next(params['teacher[member]']);
    }

    if (params.age_group) {
      this.selectedAgeGroup$.next(params.age_group);
    }

    if (params.member) {
      this.organizationMember$.next(params.member);
    }

    this.searchTerm = params.q;
    this.page = Number(params.page) || 1;

    window.scrollTo(0, 0);

    this.search(params);
  };

  private search = (params) => {
    const searchParams = { q: this.searchTerm, page: this.page, ...params };

    switch (this.kind$.value) {
      case SearchComponent.Kind.Top:
        return this.topStore.dispatch(searchTop({ q: this.searchTerm }));
      case SearchComponent.Kind.People:
        return this.peopleStore.dispatch(searchPeople({ searchParams }));
      case SearchComponent.Kind.Organizations:
        return this.organizationsStore.dispatch(
          searchOrganizations({ searchParams })
        );
      case SearchComponent.Kind.Discussions:
        return this.discussionsStore.dispatch(
          searchDiscussions({ searchParams })
        );
      case SearchComponent.Kind.Groups:
        return this.groupsStore.dispatch(searchGroups({ searchParams }));
      case SearchComponent.Kind.Clubs:
        return this.clubsStore.dispatch(searchClubs({ searchParams }));
      case SearchComponent.Kind.Events:
        return this.eventsStore.dispatch(searchEvents({ searchParams }));
      case SearchComponent.Kind.Members:
        return this.organizationStore.dispatch(
          getMembers({
            id: searchParams.org_id,
            page: searchParams.page
          })
        );
    }
  };

  private updatedQueryParams(params = {}) {
    const key = this.profileTypeToQueryParam(this.selectedProfileType$.value);

    return {
      kind: this.kind$.value,
      q: this.searchTerm,
      page: this.page,
      [key]: this.selectedProfileSubType$.value,
      age_group: this.selectedAgeGroup$.value,
      member: this.organizationMember$.value,
      ...params
    };
  }

  private updateAmountOfPages = (
    kind: SearchComponent.Kind
  ): Observable<number> => {
    return this.getObservableForKind(kind).pipe(
      map((_) => _ && this.calcAmountOfPages(_.count))
    );
  };

  private totalAmountOfResults = (
    kind: SearchComponent.Kind
  ): Observable<number> => {
    return this.getObservableForKind(kind).pipe(map((_) => _ && _.count));
  };

  private getObservableForKind(kind: SearchComponent.Kind) {
    switch (kind) {
      case SearchComponent.Kind.People:
        return this.people$;
      case SearchComponent.Kind.Organizations:
        return this.organizations$;
      case SearchComponent.Kind.Discussions:
        return this.discussions$;
      case SearchComponent.Kind.Groups:
        return this.groups$;
      case SearchComponent.Kind.Events:
        return this.events$;
      case SearchComponent.Kind.Members:
        return this.members$;
      default:
        return of(null);
    }
  }

  private calcAmountOfPages(count: number) {
    return Math.ceil(count / SearchComponent.AMOUNT_OF_USERS_ON_ONE_PAGE);
  }

  private kindConverter(kind) {
    if (!kind) {
      return SearchComponent.Kind.Top;
    }

    return kind === SearchComponent.Kind.Members
      ? SearchComponent.Kind.People
      : kind;
  }
}

export namespace SearchComponent {
  export enum Kind {
    Top = 'top',
    People = 'people',
    Organizations = 'organizations',
    Discussions = 'discussions',
    Groups = 'groups',
    Events = 'events',
    Members = 'members',
    Clubs = 'clubs'
  }

  export enum ProfileType {
    Student = 'student',
    Educator = 'educator'
  }

  export type ProfileSubType = string;
  export type Organization = string;

  export class AgeGroup {
    id: string;
    title: string;
  }
}
